import { getConfig } from "../lib/configMgr";
import { RequestBuilder, toJSON } from "./request";

/**
 * Fetch the latest PUBLISHED release note (public shape) for the current user.
 * Resolves to {} when there is none. Callers fall back to the bundled constant
 * on error/empty.
 */
export const fetchLatestReleaseNote = (authData) => {
  const { ExternalRESTAPI } = getConfig();
  const url = new URL(ExternalRESTAPI.ReleaseNotes, window.location.origin);
  return new RequestBuilder(url).withAuthorization(authData).fetch().then((r) => r.json());
};

// ─── Admin API ─────────────────────────────────────────────────────────────
// Backed by rd's /api/admin/release-notes endpoints. Every fn mirrors the
// RequestBuilder + withAuthorization idiom above and chains `.then(toJSON)` so
// callers receive a parsed body (RequestBuilder.fetch() resolves to a raw
// Response on success — see request.js / the entitlements client).

const adminBase = () => {
  const { ExternalRESTAPI } = getConfig();
  return ExternalRESTAPI.AdminReleaseNotes;
};

const adminUrl = (suffix = "") =>
  new URL(`${adminBase()}${suffix}`, window.location.origin);

/** GET /api/admin/release-notes — list every note (draft + published). */
export const fetchAdminReleaseNotes = (authData) =>
  new RequestBuilder(adminUrl())
    .withAuthorization(authData)
    .withJSONContentType()
    .fetch()
    .then(toJSON);

/**
 * POST /api/admin/release-notes/generate — LLM-draft a note from source text.
 * Highlights come back as PLAIN STRINGS; the editor promotes them to the rich
 * object shape before publish.
 * payload: {version, source_text, title?, subtitle?}
 */
export const generateReleaseNote = (authData, payload) =>
  new RequestBuilder(adminUrl("/generate"))
    .withAuthorization(authData)
    .withJSONContentType()
    .asPOST(JSON.stringify(payload))
    .fetch()
    .then(toJSON);

/**
 * POST /api/admin/release-notes — create a draft directly.
 * payload: {version, title, subtitle, highlights[]}
 */
export const createReleaseNote = (authData, payload) =>
  new RequestBuilder(adminUrl())
    .withAuthorization(authData)
    .withJSONContentType()
    .asPOST(JSON.stringify(payload))
    .fetch()
    .then(toJSON);

/**
 * PUT /api/admin/release-notes/{id} — update a draft.
 * payload: {version?, title?, subtitle?, highlights?}
 */
export const updateReleaseNote = (authData, id, payload) =>
  new RequestBuilder(adminUrl(`/${encodeURIComponent(id)}`))
    .withAuthorization(authData)
    .withJSONContentType()
    .asPUT(JSON.stringify(payload))
    .fetch()
    .then(toJSON);

/** POST /api/admin/release-notes/{id}/publish — publish a draft. */
export const publishReleaseNote = (authData, id) =>
  new RequestBuilder(adminUrl(`/${encodeURIComponent(id)}/publish`))
    .withAuthorization(authData)
    .withJSONContentType()
    .asPOST()
    .fetch()
    .then(toJSON);

/** POST /api/admin/release-notes/{id}/retract — retract a published note. */
export const retractReleaseNote = (authData, id) =>
  new RequestBuilder(adminUrl(`/${encodeURIComponent(id)}/retract`))
    .withAuthorization(authData)
    .withJSONContentType()
    .asPOST()
    .fetch()
    .then(toJSON);

export default fetchLatestReleaseNote;
